import React, { useCallback } from "react";

import { cellErrors } from "@/react/components/table/useTableMutations";
import useEditableCell from "@/react/components/table/useEditableCell";
import Select from "@8x8/oxygen-select";
import { ValidationWrapper } from "@/react/components/table/components";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";

/**
 * This component is necessary when we need to query an API in order to populate
 * the values of a select control (drop-down).
 *
 * Extracts cell related logic from options mapping, especially necessary when
 * API-based selects must be created.
 */
export const DynamicSelectCell = (props) => {
  const {
    testId,
    placeholder,
    options,
    isFetching,
    components,
    onChange,
    onBlur,
    ...cellProps
  } = props;

  const errors = cellErrors(props);
  const {
    value,
    onChange: defaultOnChange,
    onBlur: defaultOnBlur,
  } = useEditableCell(cellProps);

  const selectedValue = options.find((option) => option.value === value);

  // If searchQuery exists in options, create a custom filter to use in searches
  // Else, fallback to default react-select search behaviour
  const customFilterOption = (option, inputValue) => {
    const { label, value, searchQuery } = option.data;
    const lowerCaseInput = inputValue.toLowerCase();
    if (searchQuery) {
      return searchQuery.toLowerCase().includes(lowerCaseInput);
    }
    // Default react-select logic: check label and value
    return (
      label.toLowerCase().includes(lowerCaseInput) ||
      value.toLowerCase().includes(lowerCaseInput)
    );
  };

  // This handler must call the default onChange handler in order to update the value,
  // otherwise we will not display the updated value when the user selects a new option.
  const onChangeInternal = useCallback(
    (option) => {
      defaultOnChange(option?.value);
      onChange && onChange(option);
    },
    [defaultOnChange, onChange]
  );

  return (
    <FullWidthDiv>
      <ValidationWrapper errors={errors}>
        <Select
          size={"small"}
          menuPortalTarget={document.body}
          testId={testId}
          isDisabled={isFetching}
          options={options}
          value={selectedValue}
          onChange={onChangeInternal}
          onBlur={onBlur || defaultOnBlur}
          hasError={errors.length > 0}
          isClearable={true}
          isSearchable={true}
          placeholder={isFetching ? "Loading..." : placeholder}
          components={components}
          filterOption={customFilterOption}
        />
      </ValidationWrapper>
    </FullWidthDiv>
  );
};

export default DynamicSelectCell;
